import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  SUPPORTED_LANGS,
  HTML_LANG,
  OG_LOCALE,
  SITE_ORIGIN,
  DEFAULT_LANG,
  localePath,
  stripLangPrefix,
} from './config';

// 自分が挿入したalternateリンクだけを目印で管理する。
// これが無いと再描画のたびに他のlink要素まで巻き込んで消してしまう。
const ALT_MARK = 'data-i18n-alt';

const upsertMeta = (attrName, attrValue, content) => {
  const selector = `meta[${attrName}="${attrValue}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const replaceAlternates = (pathWithoutLang) => {
  document.head.querySelectorAll(`link[${ALT_MARK}]`).forEach((el) => el.remove());

  const add = (hreflang, href) => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    el.setAttribute('href', href);
    el.setAttribute(ALT_MARK, '');
    document.head.appendChild(el);
  };

  SUPPORTED_LANGS.forEach((code) => {
    add(HTML_LANG[code], SITE_ORIGIN + localePath(pathWithoutLang, code));
  });
  // 言語指定のない検索利用者には日本語版を返す
  add('x-default', SITE_ORIGIN + localePath(pathWithoutLang, DEFAULT_LANG));
};

/**
 * ページのtitle / description / OGP / canonical / hreflang を言語別に設定する。
 * @param {string} pageKey  翻訳リソースの meta.<pageKey> を指すキー
 * @param {object} options  title の埋め込み値と、description の上書き
 */
export function useDocumentMeta(pageKey, options = {}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { titleValues, description: descriptionOverride } = options;

  // オブジェクトを依存配列に直接入れると毎回別参照になり無限に再実行されるため、
  // 中身を文字列化して比較する。
  const titleValuesKey = JSON.stringify(titleValues ?? null);

  useEffect(() => {
    const { lang, rest } = stripLangPrefix(location.pathname);
    // 依存配列にオブジェクトを入れないよう、文字列から復元する
    const values = JSON.parse(titleValuesKey) || {};
    const title = t(`meta.${pageKey}.title`, values);
    const description =
      descriptionOverride || t(`meta.${pageKey}.description`, { defaultValue: t('meta.home.description') });
    const url = SITE_ORIGIN + localePath(rest, lang);

    document.title = title;
    upsertMeta('name', 'description', description);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:locale', OG_LOCALE[lang]);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    upsertCanonical(url);
    replaceAlternates(rest);
    // i18n.language を依存に含め、言語切替後に必ず再実行させる
  }, [pageKey, location.pathname, i18n.language, titleValuesKey, descriptionOverride, t]);
}

export default useDocumentMeta;
